import { type Map as MapLibreMap, Marker } from 'maplibre-gl'

export interface MapNavigationTarget {
  latitude: number
  longitude: number
  label: string
}

/**
 * Add a single, DOM-backed navigation target to MapLibre.
 *
 * It deliberately lives outside the entity GeoJSON source, so it never joins
 * entity clusters, filters, selections, or investigation state.
 */
export function addMapNavigationTarget(
  map: MapLibreMap,
  target: MapNavigationTarget,
  ariaLabel: string
): Marker {
  const element = buildTargetElement(target.label, ariaLabel)
  return new Marker({ element, anchor: 'center' })
    .setLngLat([target.longitude, target.latitude])
    .addTo(map)
}

function buildTargetElement(label: string, ariaLabel: string): HTMLDivElement {
  const root = document.createElement('div')
  root.className = 'relative flex size-9 items-center justify-center pointer-events-none'
  root.title = label
  root.setAttribute('role', 'img')
  root.setAttribute('aria-label', ariaLabel)

  const pulse = document.createElement('span')
  pulse.className = 'absolute inset-1 animate-ping rounded-full bg-primary/40'

  const target = document.createElement('span')
  target.className =
    'relative flex size-8 items-center justify-center rounded-full border-2 border-background bg-primary shadow-xl'

  const ring = document.createElement('span')
  ring.className =
    'flex size-4 items-center justify-center rounded-full border-2 border-primary-foreground'

  const center = document.createElement('span')
  center.className = 'size-1.5 rounded-full bg-primary-foreground'

  ring.append(center)
  target.append(ring)
  root.append(pulse, target)
  return root
}
